import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useApp } from '../App';
import { EventTypes, formatTime } from '../utils/constants';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export default function CalendarPanel() {
  const { currentTeam } = useApp();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => { if (currentTeam) loadSchedules(); }, [currentTeam]); // eslint-disable-line

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'teams', currentTeam.id, 'schedules'), orderBy('date', 'asc'));
      const snap = await getDocs(q);
      setSchedules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // 날짜 유틸
  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  };

  const isToday = (date) => isSameDay(date, new Date());

  const toDate = (ts) => ts?.toDate ? ts.toDate() : new Date(ts);

  const getSchedulesForDate = (date) => {
    return schedules.filter(s => {
      const sd = toDate(s.date);
      return isSameDay(sd, date);
    });
  };

  // 월간 그리드 생성
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay(); // 0=일요일

    const days = [];
    // 이전 달 패딩
    for (let i = 0; i < startPad; i++) days.push(null);
    // 이번 달
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    // 마지막 주 패딩
    while (days.length % 7 !== 0) days.push(null);
    return days;
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToday = () => { setCurrentMonth(new Date()); setSelectedDate(new Date()); };

  const monthStr = `${currentMonth.getFullYear()}년 ${currentMonth.getMonth() + 1}월`;
  const days = getDaysInMonth();
  const selectedSchedules = getSchedulesForDate(selectedDate);

  const formatSelectedDate = (date) => {
    return date.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, maxWidth: 1200 }}>
      {/* 캘린더 */}
      <div style={st.panel}>
        <div style={st.panelHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={prevMonth} style={st.navBtn}>◀</button>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#E8ECF1', minWidth: 140, textAlign: 'center' }}>{monthStr}</h2>
            <button onClick={nextMonth} style={st.navBtn}>▶</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={goToday} style={st.todayBtn}>오늘</button>
            <button onClick={loadSchedules} disabled={loading} style={{ ...st.navBtn, fontSize: 13 }} title="새로고침">🔄</button>
          </div>
        </div>

        <div style={{ padding: '12px 16px' }}>
          {/* 요일 헤더 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 8 }}>
            {WEEKDAYS.map((day, i) => (
              <div key={day} style={{
                textAlign: 'center', fontSize: 12, fontWeight: 600, padding: '6px 0',
                color: i === 0 ? '#EF4444' : i === 6 ? '#60A5FA' : '#667788',
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* 날짜 그리드 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {days.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} style={{ height: 64 }} />;
              const daySchedules = getSchedulesForDate(date);
              const isSelected = isSameDay(date, selectedDate);
              const isTodayDate = isToday(date);
              const weekday = date.getDay();
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();

              return (
                <div
                  key={idx}
                  onClick={() => setSelectedDate(date)}
                  style={{
                    height: 64, borderRadius: 8, cursor: 'pointer', padding: '4px 2px',
                    textAlign: 'center', position: 'relative',
                    backgroundColor: isSelected ? 'rgba(91,196,216,0.12)' : 'transparent',
                    border: isTodayDate ? '2px solid #5BC4D8' : '2px solid transparent',
                    opacity: isCurrentMonth ? 1 : 0.3,
                    transition: 'all 0.1s',
                  }}
                >
                  <div style={{
                    fontSize: 14, fontWeight: isTodayDate ? 700 : 400,
                    color: weekday === 0 ? '#EF4444' : weekday === 6 ? '#60A5FA' : '#C8D8E8',
                    marginBottom: 4,
                  }}>
                    {date.getDate()}
                  </div>

                  {/* 일정 인디케이터 (최대 3개 도트) */}
                  {daySchedules.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                      {daySchedules.slice(0, 3).map((s, i) => (
                        <div key={i} style={{
                          width: 6, height: 6, borderRadius: '50%',
                          backgroundColor: EventTypes[s.eventType]?.color || '#5BC4D8',
                        }} />
                      ))}
                    </div>
                  )}

                  {/* 4개 이상이면 숫자 표시 */}
                  {daySchedules.length > 3 && (
                    <div style={{ fontSize: 9, color: '#667788', marginTop: 1 }}>+{daySchedules.length - 3}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 이벤트 유형 범례 */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16, padding: '8px 0', flexWrap: 'wrap' }}>
            {Object.entries(EventTypes).map(([key, et]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: et.color }} />
                <span style={{ fontSize: 10, color: '#778899' }}>{et.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 선택된 날짜 일정 */}
      <div style={st.panel}>
        <div style={st.panelHeader}>
          <h2 style={st.panelTitle}>📋 {formatSelectedDate(selectedDate)}</h2>
        </div>
        <div style={st.panelBody}>
          {loading ? <div style={st.empty}>로딩 중...</div> :
            selectedSchedules.length === 0 ? (
              <div style={st.empty}>
                <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.5 }}>📅</div>
                일정이 없습니다
              </div>
            ) : (
              <div>
                {selectedSchedules.map(schedule => {
                  const et = EventTypes[schedule.eventType] || {};
                  const schedDate = toDate(schedule.date);
                  return (
                    <div key={schedule.id} style={{
                      padding: '12px 14px', marginBottom: 6, borderRadius: 8,
                      borderLeft: `4px solid ${et.color || '#5BC4D8'}`,
                      backgroundColor: 'rgba(255,255,255,0.02)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{schedule.title}</span>
                        {et.label && (
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, border: `1px solid ${et.color}`, color: et.color, fontWeight: 600 }}>
                            {et.label}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#778899' }}>
                        <span>⏰ {formatTime(schedule.date)}</span>
                        {schedule.location && <span>📍 {schedule.location}</span>}
                      </div>
                      {schedule.fee > 0 && (
                        <div style={{ fontSize: 11, color: '#667788', marginTop: 4 }}>💰 {schedule.fee.toLocaleString()}원</div>
                      )}
                      {schedule.opponentTeamName && (
                        <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>🏒 vs {schedule.opponentTeamName}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}

const st = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  panelHeader: { padding: '12px 16px', borderBottom: '1px solid rgba(91,196,216,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  panelTitle: { margin: 0, fontSize: 14, fontWeight: 600 },
  panelBody: { padding: 10, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '40px 16px', color: '#556677', fontSize: 13 },
  navBtn: { padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 14, cursor: 'pointer' },
  todayBtn: { padding: '4px 12px', borderRadius: 6, border: 'none', backgroundColor: 'rgba(91,196,216,0.15)', color: '#5BC4D8', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
};
